// 小説の描写から LLM が推定したキャラの見た目を、3D 生成とゲーム JSON で
// 共通利用できる安定した構造へ正規化する純粋関数群。数値・分類は story-pipeline.json 駆動。

const isPlayable = (role) => role === 'protagonist' || role === 'partner';

function finite(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function round(value, digits) {
    const factor = 10 ** digits;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

function cleanText(value, maxLength) {
    return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

/** LLM 出力の visualProfile を許容範囲内の自己完結データへ正規化する。 */
export function normalizeVisualProfile(cast, rawProfile, config) {
    const raw = rawProfile && typeof rawProfile === 'object' ? rawProfile : {};
    const digits = Number.isInteger(config.roundDigits) ? config.roundDigits : 4;
    const maxTextLength = Number.isInteger(config.maxTextLength) ? config.maxTextLength : 160;
    const range = cast?.humanoid === false ? config.heightRanges.nonHumanoid : config.heightRanges.humanoid;
    const defaultHeight = finite(config.defaultHeightMetersByRole?.[cast?.role])
        ?? finite(config.defaultHeightMetersByRole?.npc)
        ?? 1.7;
    const rawHeight = finite(raw.heightMeters);
    const heightMeters = round(clamp(rawHeight ?? defaultHeight, Number(range.min), Number(range.max)), digits);
    const allowedBuilds = new Set(config.allowedBuilds ?? []);
    const build = allowedBuilds.has(raw.build) ? raw.build : config.defaultBuild;
    const allowedBases = new Set(config.allowedHeightBases ?? []);
    const heightBasis = allowedBases.has(raw.heightBasis)
        ? raw.heightBasis
        : (rawHeight == null ? 'default' : 'inferred');
    const riggingHeightMeters = round(Number(config.riggingHeightMeters), digits);
    const displayHeight = round(heightMeters * Number(config.gameDisplayHeightPerMeter), digits);
    const uniformScale = round(heightMeters / riggingHeightMeters, digits);
    const radiusMultiplier = finite(config.colliderRadiusBuildMultiplier?.[build]) ?? 1;
    const colliderRadius = round(heightMeters * Number(config.colliderRadiusRatio) * radiusMultiplier, digits);
    const headLabelOffsetY = round(heightMeters * Number(config.headLabelOffsetRatio), digits);
    // 名前ラベル・HPバーのワールド固定オフセット（足元原点＋実身長＋マージン）。
    // Meshy リグ済み GLB はバインドポーズ bbox が実表示と一致せず、bbox 由来のオフセットでは
    // バーが顔・頭に重なるため、プレイアブル／companion NPC とも「頭上の相対位置」規則へ統一する。
    const headLabelWorldYAboveOrigin = round(heightMeters + (finite(config.headLabelWorldYMarginMeters) ?? 0.3), digits);
    // プレイヤー操作中は常にカメラ近くで同じ世界距離の隙間が大きく見えるため（ペット近接時と同じ
    // perspective 現象）、プレイヤー専用のマージンを別に持つ（未指定は NPC 用マージンへフォールバック）。
    const playerHeadLabelWorldYAboveOrigin = round(
        heightMeters + (finite(config.playerHeadLabelWorldYMarginMeters) ?? finite(config.headLabelWorldYMarginMeters) ?? 0.5),
        digits,
    );

    return {
        formatVersion: config.formatVersion ?? '1.0',
        heightMeters,
        heightBasis,
        build,
        apparentAge: cleanText(raw.apparentAge, maxTextLength),
        proportions: cleanText(raw.proportions, maxTextLength),
        riggingHeightMeters,
        game: {
            scaleMode: 'uniform',
            uniformScale,
            displayHeight,
            colliderHeight: heightMeters,
            colliderRadius,
            headLabelOffsetY,
            headLabelWorldYAboveOrigin,
            playerHeadLabelWorldYAboveOrigin,
        },
    };
}

/** プレイアブルの見た目プロファイルを characters.json の最終表示設定へ反映する。 */
export function applyVisualProfilesToCharacters(charactersDocument, castById, profilesById) {
    if (!charactersDocument || !Array.isArray(charactersDocument.characters)) return 0;
    let updated = 0;
    for (const character of charactersDocument.characters) {
        const cast = castById.get(character.id);
        const profile = profilesById.get(character.id);
        if (!cast || !profile || !isPlayable(cast.role)) continue;
        character.visualProfile = profile;
        character.riggingHeightMeters = profile.riggingHeightMeters;
        character.displayHeight = profile.game.displayHeight;
        character.headLabelOffsetY = profile.game.headLabelOffsetY;
        character.headLabelWorldYAboveOrigin = profile.game.headLabelWorldYAboveOrigin;
        character.playerHeadLabelWorldYAboveOrigin = profile.game.playerHeadLabelWorldYAboveOrigin;
        character.collider = {
            ...(character.collider ?? {}),
            height: profile.game.colliderHeight,
            radius: profile.game.colliderRadius,
        };
        updated += 1;
    }
    return updated;
}

/** 個別プロンプト MD へ埋め込む、後工程でコピー可能な JSON を返す。 */
export function visualProfileJson(profile) {
    return JSON.stringify(profile, null, 2);
}
